import React from "react";
import { Star, Sparkles, Quote, ExternalLink } from "lucide-react";
import { DEFAULT_TESTIMONIALS } from "../components/TestimonialCarousel";
import CTABanner from "../components/CTABanner";
import PageMeta from "../components/PageMeta";

const initials = (name) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

const ACCENT = "from-emerald-500 to-teal-600";

const TestimonialCard = ({ t }) => (
  <figure className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white p-5 ring-1 ring-slate-200 transition-all duration-300 hover:-translate-y-1 hover:ring-emerald-200 hover:shadow-[0_24px_50px_-25px_rgba(16,185,129,0.35)]">
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${ACCENT}`} />

    <div className="relative flex items-center gap-1 text-amber-500">
      {Array.from({ length: t.stars }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-amber-400" />
      ))}
    </div>

    <blockquote className="relative mt-3 flex-1 text-[13.5px] leading-relaxed text-slate-700">
      <Quote className="mb-1.5 h-4 w-4 text-emerald-500/70" />
      {t.quote}
    </blockquote>

    <figcaption className="relative mt-4 flex items-center gap-3 border-t border-slate-100 pt-4">
      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br ${ACCENT} font-serif text-xs font-semibold text-white shadow-md`}>
        {initials(t.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-[#0A2540]">{t.name}</div>
        <div className="truncate text-[11px] text-slate-500">{t.role}</div>
      </div>
    </figcaption>

    {t.wiki ? (
      <a
        href={t.wiki}
        target="_blank"
        rel="noopener noreferrer"
        className="relative mt-3 inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
        data-testid={`grid-visit-${initials(t.name).toLowerCase()}`}
      >
        Visit Wikipedia page <ExternalLink className="h-3 w-3" />
      </a>
    ) : null}
  </figure>
);

export default function Testimonials() {
  return (
    <div className="overflow-hidden" data-testid="testimonials-page">
      <PageMeta
        title="Client Testimonials — Prime Wiki Services"
        description="Meet the real Wikipedia subjects Prime Wiki Services has published — from founders and executives to technology companies and design studios. Every review links to the live page."
        keywords="prime wiki services reviews, wikipedia agency testimonials, wikipedia page success stories, verified wikipedia clients"
      />

      {/* 1. Hero */}
      <section className="mwb-hero-bg px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" /> Client stories
          </span>
          <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">
            Real pages. Real clients. Real longevity.
          </h1>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">
            A selection of published Wikipedia pages we've worked on — each testimonial links straight to the live article so you can see the outcome for yourself.
          </p>
        </div>
      </section>

      {/* 2. Informative intro */}
      <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">What the numbers say</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Average client stays with us for 3 years.</h2>
            <p className="mt-4 text-base text-slate-600">Once a Wikipedia page is live, keeping it alive is a long game. 72% of our clients stay on maintenance retainer past year one, and 41% renew into year three. That retention is the single most honest testimonial we can offer.</p>
            <p className="mt-3 text-base text-slate-600">Every quote below is tied to a live Wikipedia article — click "Visit Wikipedia page" on any card to read the published result directly.</p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&w=1200&q=80"
              alt="A client and a specialist finalising a Wikipedia engagement over a handshake"
              className="h-[320px] w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* 3. GRID of real Wikipedia subjects */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="testimonial-grid">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Every page verified</p>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Clients who chose credibility.</h2>
          <p className="mt-3 text-base text-slate-600">
            Each card below corresponds to a real, live Wikipedia article. Open any page to see the outcome for yourself.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {DEFAULT_TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={`${t.name}-${i}`} t={t} />
          ))}
        </div>
      </section>

      {/* 4. Single final CTA — matches homepage design */}
      <CTABanner />
    </div>
  );
}
